/*
 * Copyright (C) 2005 - 2023. Cloud Software Group, Inc. All Rights Reserved. Confidential & Proprietary.
 * Licensed pursuant to commercial Cloud Software Group, Inc. End User License Agreement.
 */

import Highcharts from 'highcharts-pack/highcharts/highcharts';

import GroupedCategoriesPlugin from 'highcharts-pack/highcharts/grouped-categories';
import HighchartsMorePlugin from 'highcharts-pack/highcharts/highcharts-more';
import HighchartsAccessibilityPlugin from 'highcharts-pack/highcharts/highcharts-accessibility';
import HeatMapChartsPlugin from 'highcharts-pack/highcharts/highcharts-heatmap';
import TreeMapChartsPlugin from 'highcharts-pack/highcharts/highcharts-treemap';
import SolidChartsPlugin from 'highcharts-pack/highcharts/highcharts-solid-gauge';
import Highcharts3DPlugin from 'highcharts-pack/highcharts/highcharts-3d';
import MapsPlugin from 'highcharts-pack/highcharts/highmaps-map';
import TilemapChartsPlugin from 'highcharts-pack/highcharts/highmaps-tilemap';

let doLibraryInitialization = true;

if (window.__hc_is_loaded_by_jrl) {
    // jasperreports-highcharts (JRL) library uses code from bi-adhoc to generate config to render charts, as a result
    // this file get loaded by JRL. It would be fine, but JRL loads HC library and initializes it with default settings
    // before using this file, which creates a conflicts because we are trying to make the same.
    // To avoid doing the same action twice there is a special global variable which indicates that JRL has loaded HC
    // library.
    // Now, you may ask why we still import all HC plugins ? It's because the of the js linter, it requires us to put
    // all imports at the top of the file, but these imports do nothing, because, JRL uses file
    // adhocHighchartsSettingService.js, which by dependency chain loads current file. So, JRL actually uses file
    // adhocHighchartsSettingService not directly, but a built version of this file by webpack, and there is a special
    // webpack configuration file webpack.config.export.js which changes paths for all HC library files, so in the end
    // all of them are replaced with file globalHighchartsProvider.js, which sets global variable __hc_is_loaded_by_jrl
    // and returns HC library which is declared as global variable, and which is already loaded by JRL.
    // Yeah, sounds complicated.
    doLibraryInitialization = false;
}

if (doLibraryInitialization) {

    // add the plugins to Highcharts
    GroupedCategoriesPlugin(Highcharts);
    HighchartsMorePlugin(Highcharts);
    HighchartsAccessibilityPlugin(Highcharts);
    HeatMapChartsPlugin(Highcharts);
    TreeMapChartsPlugin(Highcharts);
    SolidChartsPlugin(Highcharts);
    Highcharts3DPlugin(Highcharts);
    MapsPlugin(Highcharts);
    TilemapChartsPlugin(Highcharts);

    /*
        This Pie title plugin based on http://jsfiddle.net/highcharts/tnSRA/.
        It allows to have chart titles be attached next to each pie in multi-pie chart.
     */
    Highcharts.wrap(Highcharts.seriesTypes.pie.prototype, 'render', function (proceed) {

        const chart = this.chart,
            center = this.center || (this.yAxis && this.yAxis.center),
            titleOption = this.options.title;

        proceed.call(this);

        if (center && titleOption) {
            const box = {
                x: chart.plotLeft + center[0] - 0.5 * center[2],
                y: chart.plotTop + center[1] - 0.5 * center[2],
                width: center[2],
                height: center[2]
            };
            if (!this.title) {
                if (!titleOption.style) {
                    titleOption.style = {};
                }

                this.title = this.chart.renderer.label(titleOption.text)
                    .css(titleOption.style)
                    .add()
                    .align(titleOption, null, box);
            } else {
                this.title.align(titleOption, null, box);
            }
        }
    });

    // Highcharts code has a bug which prevents us from rendering Time Series Heat Map charts, which is logged here:
    // https://github.com/highcharts/highcharts/issues/17092
    // and there is a workaround while this bug is not yet fixed.
    Highcharts._modules['Core/Axis/DateTimeAxis.js'].Additions.prototype.getXDateFormat = function (x, dateTimeLabelFormats) {
        const axis = this.axis;
        if (axis.closestPointRange) {
            return axis.chart.time.getDateFormat(axis.closestPointRange, x, axis.options.startOfWeek, dateTimeLabelFormats) || dateTimeLabelFormats.year;
        }
        return dateTimeLabelFormats.day.main || dateTimeLabelFormats.day;
    };
}

export default Highcharts;
